<template>
  <button
    class="progress-ring"
    @click="emitClickEvent"
    @mouseover="iconEnter"
    @mouseleave="iconLeave">
    <svg class="progress-ring__svg"
         :width="radius * 2"
         :height="radius * 2">
      <circle
        :class="['progress-ring__circle', { 'progress-animation': !paused }]"
        :style="circleStyle"
        :stroke-width="thickness"
        :stroke="color"
        fill="transparent"
        :r="normalizedRadius"
        :cx="radius"
        :cy="radius" />
    </svg>
    <font-awesome-icon
      aria-hidden="true"
      :styl="iconStyle"
      :class="[
        'progress-ring__icon',
        {
          'progress-ring__icon--visible': pauseIcon && paused,
          'progress-ring__icon--hover': ui.icon.hover,
        },
      ]"
      :icon="animationIcon">
    </font-awesome-icon>
  </button>
</template>

<script>
export default {
  name: "ProgressRing",
  props: {
    radius: {
      type: Number,
      default: 120,
    },
    thickness: {
      type: Number,
      default: 1,
    },
    color: {
      type: String,
      default: "red",
    },
    animationDuration: {
      type: Number,
      default: 5000,
    },
    paused: {
      type: Boolean,
      default: false,
    },
    pauseIcon: {
      type: Boolean,
      default: false,
    },
    autoPlay: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      pathLength: 0,
      animationPlayState: null,
      webkitAnimation: "",
      ui: {
        icon: {
          hover: false,
        },
      },
      icons: {
        play: ["fas", "play"],
        pause: ["fas", "pause"],
      },
    };
  },
  computed: {
    normalizedRadius () {
      return this.radius - this.thickness * 2;
    },
    circleStyle () {
      return {
        "stroke-dashoffset": `${this.pathLength}`,
        "stroke-dasharray": `${this.pathLength}`,
        "animation-duration": `${this.animationDuration / 1000}s`,
        "animation-play-state": this.animationPlayState,
      };
    },
    iconStyle () {
      return {
        color: this.color,
      };
    },
    animationIcon () {
      let icon;

      if ( this.autoPlay === false && this.ui.icon.hover ) {
        icon = this.icons.play;
      } else {
        icon = this.icons.pause;
      }
      return icon;
    },
  },
  watch: {
    paused: {
      immediate: true,
      handler ( val ) {
        if ( val ) {
          this.animationPlayState = "paused";
        } else {
          this.animationPlayState = null;
        }
      },
    },
  },
  destroyed () {
    window.removeEventListener( "focus", this.resetAnimation );
  },
  mounted () {
    window.addEventListener( "focus", this.resetAnimation );

    const path = document.querySelector( ".progress-ring__circle" );
    try {
      this.pathLength = path.getTotalLength();
    } catch {
      const radius = Number( path.getAttribute( "r" ) );
      this.pathLength = 2 * Math.PI * radius;
    }
  },
  methods: {
    iconEnter () {
      this.ui.icon.hover = true;
    },
    iconLeave () {
      this.ui.icon.hover = false;
    },
    emitClickEvent () {
      this.$emit( "clicked" );
    },
    resetAnimation () {
      // Reset the css animation to keep sync with the autoplay slide interval.
      const path = document.querySelector( ".progress-animation" );

      if ( path ) {
        path.style.webkitAnimation = "none";
        window.setTimeout( () => {
          path.style.webkitAnimation = "";
          path.style.animationPlayState = this.animationPlayState;
        }, 10 );
      }
    },
  },
};
</script>

<style lang="less" scoped>
.progress-ring {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background: none;
  outline: 0;
  border: 0;

  &:focus {
    outline: 1px auto #000;
  }

  &__svg {
    filter: drop-shadow(0 0 10px rgba(0, 0, 0, 0.5));
  }

  &__circle {
    transform: rotate(-90deg);
    transform-origin: 50% 50%;
  }

  &__icon {
    position: absolute;
    color: #fff;
    font-size: 1.13rem;
    opacity: 0;
    transition: opacity 0.3s ease-out;

    &--visible {
      opacity: 1;
    }

    &--hover {
      color: #d71920;
    }
  }

  .progress-animation {
    animation: dash 5s linear infinite;
  }
}

@keyframes dash {
  to {
    stroke-dashoffset: 0;
  }
}
</style>
